import { useEffect, useState } from "react";
import { MagnifyingGlass, DownloadSimple } from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PaymentRow } from "@/components/PaymentRow";
import { PaypalWebhookCard } from "@/components/PaypalWebhookCard";

const STATUSES = [["", "All completed & changed"], ["paid", "Paid"], ["capture_pending", "Pending"], ["disputed", "Disputed"],
  ["refunded", "Refunded"], ["reversed", "Reversed"], ["refunded_duplicate", "Duplicate refunded"], ["failed", "Failed"], ["pending", "Started, not paid"]];
const select = "h-11 rounded-full border border-[#4A4540]/15 bg-white px-4 text-sm text-[#2C2A28]";

const toCsv = (orders) => {
  const cols = ["created_at", "order_number", "username", "email", "product_label", "year", "amount", "currency", "provider",
    "payment_status", "paypal_order_id", "paypal_capture_id", "stripe_payment_intent_id", "paid_at", "refunded_at"];
  const esc = (v) => `"${String(v ?? "").replaceAll('"', '""')}"`;
  const rows = orders.flatMap((o) => [cols.map((c) => esc(o[c])).join(",") + "," + esc(""),
    ...o.changes.map((ch) => cols.map(() => '""').join(",") + "," + esc(`${ch.at} | ${ch.event_type} | ${ch.action} | ${ch.reason} | ${ch.transaction_id || ""}`))]);
  return [cols.join(",") + ",change", ...rows].join("\n");
};

const Stat = ({ label, value, testid }) => (
  <div className="rounded-2xl bg-gradient-to-br from-[#FFF4D6] to-[#FFE9C2] px-4 py-3">
    <p className="text-xs text-[#8A5A2B]">{label}</p>
    <p data-testid={testid} className="font-serif text-2xl text-[#2C2A28]">{value}</p>
  </div>
);

export default function PaymentHistory() {
  const { user } = useAuth();
  const [filters, setFilters] = useState({ status: "", provider: "", q: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user?.role !== "admin") return;
    const t = setTimeout(() => {
      api.get("/admin/payments/history", { params: filters })
        .then((r) => { setData(r.data); setError(""); })
        .catch((e) => setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load payments."));
    }, 300);
    return () => clearTimeout(t);
  }, [filters, user]);

  if (user && user.role !== "admin")
    return <p data-testid="payment-history-owner-only" className="p-10 text-center text-[#4A4540]">This page is for the site owner only.</p>;

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const download = () => {
    const url = URL.createObjectURL(new Blob([toCsv(data.orders)], { type: "text/csv" }));
    Object.assign(document.createElement("a"), { href: url, download: "payment-history.csv" }).click();
    URL.revokeObjectURL(url);
  };
  const s = data?.summary;

  return (
    <main data-testid="payment-history-page" className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <h1 className="font-serif text-4xl text-[#2C2A28] sm:text-5xl">Payment history</h1>
      <p className="mt-2 text-sm text-[#8A7F76] md:text-base">Every payment, refund, dispute and access change, with its PayPal transaction ID. Owner only.</p>
      <PaypalWebhookCard />
      {s && (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-5">
          <Stat label="Paid orders" value={s.paid} testid="stat-paid" />
          <Stat label="Paid total" value={`A$${s.paid_total.toFixed(2)}`} testid="stat-paid-total" />
          <Stat label="Refunded / reversed" value={s.refunded} testid="stat-refunded" />
          <Stat label="Open disputes" value={s.disputed} testid="stat-disputed" />
          <Stat label="Pending at PayPal" value={s.pending} testid="stat-pending" />
        </div>
      )}
      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center">
        <label className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#8A7F76]" />
          <input data-testid="payment-search-input" value={filters.q} onChange={set("q")} placeholder="Order, username, email or transaction ID"
            className="h-11 w-full rounded-full border border-[#4A4540]/15 bg-white pl-11 pr-4 text-sm" />
        </label>
        <select data-testid="payment-status-filter" value={filters.status} onChange={set("status")} className={select}>
          {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select data-testid="payment-provider-filter" value={filters.provider} onChange={set("provider")} className={select}>
          <option value="">All providers</option><option value="paypal">PayPal</option><option value="stripe">Stripe</option>
        </select>
        <button data-testid="payment-export-btn" onClick={download} disabled={!data?.orders.length}
          className="flex h-11 items-center justify-center gap-2 rounded-full bg-[#E06D53] px-5 text-sm font-semibold text-white hover:bg-[#C95A42] disabled:opacity-50">
          <DownloadSimple size={18} /> CSV
        </button>
      </div>
      {error && <p data-testid="payment-history-error" className="mt-6 text-[#A84A32]">{error}</p>}
      {data && !data.orders.length && <p data-testid="payment-history-empty" className="mt-10 text-center text-[#8A7F76]">No payments match these filters.</p>}
      <ul data-testid="payment-history-list" className="mt-6 space-y-3">
        {data?.orders.map((o) => <PaymentRow key={o.order_number} o={o} />)}
      </ul>
    </main>
  );
}
